import { useMemo } from 'react';
import { useQueryKiosksListByProjectID } from '../../../../app/kiosk-connectivity-status-bar/kiosk.queries';
import { useQueryListHubs } from '../../../lockers/hubs.queries';
import { useQueryGetProjectChildProjects } from '../../../../core/queries/projects.queries';

/**
 * Hook to fetch and map kiosk data to project and hub information
 * Used by both kiosk list and detail view components
 */
export function useKioskProjectMapping(projectID: string) {
  // Fetch all kiosks for the project (including child projects)
  const { data: kiosks, isLoading: kiosksLoading } = useQueryKiosksListByProjectID(projectID, {
    includeChildProjects: true,
    includeInactive: false
  });

  // Fetch all hubs for the project (including child projects)
  const { data: hubs, isLoading: hubsLoading } = useQueryListHubs(projectID, {
    includeChildProjects: true
  });

  // Fetch all child projects (includes parent project)
  const { data: childProjects, isLoading: childProjectsLoading } = useQueryGetProjectChildProjects(projectID);

  // Build hubID → projectID map
  const hubIdToProjectId = useMemo(() => {
    if (!hubs) {
      return new Map<string, string>();
    }
    return new Map(hubs.map((hub) => [hub.id, hub.managedByProjectID]));
  }, [hubs]);

  // Build projectID → projectName map from child projects
  const projectIdToProjectName = useMemo(() => {
    if (!childProjects) {
      return new Map<string, string>();
    }
    return new Map(childProjects.map((project) => [project.id, project.name]));
  }, [childProjects]);

  // Build kioskID → hubName map
  const kioskIdToHubName = useMemo(() => {
    if (!kiosks) {
      return new Map<string, string>();
    }
    return new Map(kiosks.map((kiosk) => [kiosk.iotHubID ?? kiosk.id, kiosk.hubName || 'Unknown hub']));
  }, [kiosks]);

  // Build kioskID → projectName map for all kiosks
  const kioskIdToProjectName = useMemo(() => {
    if (!kiosks || !hubIdToProjectId.size || !projectIdToProjectName.size) {
      return new Map<string, string>();
    }
    const map = new Map<string, string>();
    kiosks.forEach((kiosk) => {
      const hubId = kiosk.hubID;
      const projectId = hubIdToProjectId.get(hubId);
      if (projectId) {
        const projectName = projectIdToProjectName.get(projectId) || 'Unknown project';
        // Use iotHubID as the key to match with kioskStates
        const kioskKey = kiosk.iotHubID ?? kiosk.id;
        map.set(kioskKey, projectName);
      }
    });
    return map;
  }, [kiosks, hubIdToProjectId, projectIdToProjectName]);

  // Helper to get project name for a specific kiosk ID
  const getProjectNameForKiosk = useMemo(() => {
    return (kioskId: string) => {
      return kioskIdToProjectName.get(kioskId) || 'Unknown project';
    };
  }, [kioskIdToProjectName]);

  // Helper to get hub name for a specific kiosk ID
  const getHubNameForKiosk = useMemo(() => {
    return (kioskId: string) => {
      return kioskIdToHubName.get(kioskId) || 'Unknown hub';
    };
  }, [kioskIdToHubName]);

  const hasRequiredData = !!kiosks && !!hubs && !!childProjects;
  const isLoading = kiosksLoading || hubsLoading || childProjectsLoading;

  return {
    kiosks,
    hubs,
    childProjects,
    hubIdToProjectId,
    projectIdToProjectName,
    kioskIdToHubName,
    kioskIdToProjectName,
    getProjectNameForKiosk,
    getHubNameForKiosk,
    hasRequiredData,
    isLoading
  };
}
